import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';
import { ExtensionAuthErrors } from '@/lib/errors';
import { isValidExtensionId, syncExtension } from '@/lib/auth/extension-sync';

interface AuthSyncState {
  status: 'checking' | 'syncing' | 'success' | 'error';
  error?: string;
  extensionId?: string;
}

export function ExtensionAuth() {
  const [state, setState] = useState<AuthSyncState>({ status: 'checking' });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const syncAuth = async () => {
      try {
        // 1. Get extension ID from URL
        const extensionId = searchParams.get('extensionId');
        if (!extensionId) {
          setState({ 
            status: 'error', 
            error: ExtensionAuthErrors.NO_EXTENSION_ID 
          });
          return;
        }

        // 2. Verify extension ID is valid
        if (!isValidExtensionId(extensionId)) {
          setState({ 
            status: 'error', 
            error: ExtensionAuthErrors.INVALID_EXTENSION_ID 
          });
          return;
        }

        // 3. Get current session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          // Store extension ID in localStorage for after login
          localStorage.setItem('pendingExtensionAuth', extensionId);
          navigate('/');
          return;
        }

        setState({ status: 'syncing', extensionId });

        // 4. Send auth state to extension
        await syncExtension(extensionId, session);
        setState({ status: 'success', extensionId });

      } catch (error) {
        console.error('Extension auth sync failed:', error);
        setState({ 
          status: 'error', 
          error: error instanceof Error ? error.message : ExtensionAuthErrors.UNKNOWN,
          extensionId: searchParams.get('extensionId') || undefined
        });
      }
    };

    syncAuth();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-8">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <span className="font-extrabold text-[26px] leading-[26px] tracking-[-0.06em] text-primary">
            Solem
          </span>
        </div>

        {/* Status Content */}
        {state.status === 'checking' && (
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
            <p className="mt-4 text-muted-foreground">Checking authentication status...</p>
          </div>
        )}

        {state.status === 'syncing' && (
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
            <p className="mt-4 text-muted-foreground">Connecting to extension...</p>
            <p className="mt-2 text-sm text-muted-foreground">Extension ID: {state.extensionId}</p>
          </div>
        )}

        {state.status === 'success' && (
          <div className="text-center">
            <div className="rounded-full h-12 w-12 bg-green-100 flex items-center justify-center mx-auto">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mt-4 text-xl font-semibold">Successfully Connected!</h2>
            <p className="mt-2 text-muted-foreground">
              You can now close this tab and return to the extension.
            </p>
            <Button
              onClick={() => window.close()}
              className="mt-6"
            >
              Close Tab
            </Button>
          </div>
        )}

        {state.status === 'error' && (
          <div className="text-center">
            <div className="rounded-full h-12 w-12 bg-destructive/10 flex items-center justify-center mx-auto">
              <svg className="h-6 w-6 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="mt-4 text-xl font-semibold">Connection Failed</h2>
            <div className="mt-4 p-4 bg-destructive/10 rounded-lg">
              <p className="text-destructive">{state.error}</p>
              {state.extensionId && (
                <p className="mt-2 text-sm text-destructive/80">
                  Extension ID: {state.extensionId}
                </p>
              )}
            </div>
            <Button
              onClick={() => window.location.reload()}
              className="mt-6"
              variant="outline"
            >
              Try Again
            </Button>
          </div>
        )}
      </div>
    </div>
  );
} 